"use client"

import clsx from "clsx"
import Link from "next/link"
import React from "react"

import { useRouterScroll } from "@/hooks/useScroll"

import ArrowLeft from "../../../../public/icons/arrow-left.svg"
import ArrowRight from "../../../../public/icons/arrow-right.svg"

interface PaginationProps {
  totalPages: number
  currentPage: number
  href: string
  scrollTo?: string // Optional scrollTo parameter
}

const Pagination: React.FC<PaginationProps> = ({
  totalPages,
  currentPage,
  href,
  scrollTo,
}) => {
  const { scrollToAfterNavigation } = useRouterScroll()

  const renderPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 9

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else if (currentPage <= 2) {
      for (let i = 1; i <= Math.min(3, totalPages); i++) {
        pages.push(i)
      }
      pages.push("...")
      pages.push(totalPages - 1, totalPages)
    } else if (currentPage <= 4) {
      for (let i = 1; i <= Math.min(5, totalPages); i++) {
        pages.push(i)
      }
      pages.push("...")
      pages.push(totalPages - 1, totalPages)
    } else if (currentPage >= totalPages - 3) {
      pages.push(1, 2)
      pages.push("...")
      for (let i = totalPages - 4; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      pages.push(1, 2)
      pages.push("...")
      pages.push(currentPage - 1, currentPage, currentPage + 1)
      pages.push("...")
      pages.push(totalPages - 1, totalPages)
    }

    return pages
  }

  const pages = renderPageNumbers()

  const handleClick = () => {
    if (scrollTo) {
      scrollToAfterNavigation(scrollTo, {
        duration: 1000,
        delay: 500, // Increased delay to account for navigation
        smooth: true,
      })
    }
  }

  return (
    <div className="flex items-center justify-center w-full h-10 text-sm select-none">
      {currentPage > 1 ? (
        <Link
          href={`${href}${currentPage - 1}`}
          scroll={false}
          passHref
          onClick={handleClick}
        >
          <div
            className={clsx(
              "flex items-center mr-8 text-gray-2.5 hover:text-gray-2.5 focus:outline-none cursor-pointer",
            )}
          >
            <ArrowLeft className="mr-8 w-3 block h-4.5 text-white-100" />
          </div>
        </Link>
      ) : (
        <div className="flex items-center mr-8 text-gray-30 cursor-not-allowed">
          <ArrowLeft className="mr-8 w-3 block h-4.5 text-white-100" />
        </div>
      )}

      <div className="flex items-center justify-center">
        {pages.map((page) =>
          page === "..." ? (
            <div
              key={`ellipsis-${Math.random()}`}
              className="w-10 px-0.5 text-center text-white-100"
            >
              ...
            </div>
          ) : (
            <Link
              key={page}
              href={`${href}${page}`}
              scroll={false}
              passHref
              onClick={handleClick}
            >
              <div
                className={clsx(
                  "flex items-center justify-center w-10 h-10 text-lg rounded-full cursor-pointer",
                  {
                    "text-gray-2.5 bg-opacity-0": currentPage === page,
                    "text-gray-2.5 opacity-50": currentPage !== page,
                  },
                )}
              >
                {page}
              </div>
            </Link>
          ),
        )}
      </div>

      {currentPage < totalPages ? (
        <Link
          href={`${href}${currentPage + 1}`}
          scroll={false}
          passHref
          onClick={handleClick}
        >
          <div
            className={clsx(
              "flex items-center ml-8 text-gray-2.5 hover:text-gray-2.5 focus:outline-none cursor-pointer",
            )}
          >
            <ArrowRight className="ml-8 w-3 block h-4.5 text-white-100" />
          </div>
        </Link>
      ) : (
        <div className="flex items-center ml-8 text-gray-30 cursor-not-allowed">
          <ArrowRight className=" w-3 block h-4.5 text-white-100" />
        </div>
      )}
    </div>
  )
}

export default Pagination
